import React, { useEffect, useState, useContext, useRef } from "react";
import { Offcanvas } from "react-bootstrap";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import StarekContext from "../../context/StarekContext";

const JobSchedulesSidebar = ({
    isOpen,
    onClose,
    onSubmit,
    type = "add",
    jobData = null,
}) => {
    const starekContext = useContext(StarekContext);
    const [formData, setFormData] = useState({
        name: "",
        part: "",
        cycle: "",
        device: "",
        dateRange: [new Date(), new Date()],
    });
    const [jobParts, setJobParts] = useState([]);
    const [jobCycles, setJobCycles] = useState([]);
    const [jobDevices, setJobDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const previousNameRef = useRef("");

    const lastUsedPlant = starekContext.lastUsedPlant;
    const lastUsedWorkspace = starekContext.lastUsedWorkspace;
    const { host, scheme, port } = lastUsedWorkspace.engine;
    const token = starekContext.auth;
    const apiUrl = `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`;

    useEffect(() => {
        if (type === "edit" && jobData) {
            const start = new Date(jobData.start_time);
            const end = new Date(jobData.end_time);
            setFormData({
                name: jobData.name || "",
                part: jobData.part_id || "",
                cycle: jobData.cycle_id || "",
                device: jobData.device_id || "",
                dateRange: [start, end],
            });
        } else {
            setFormData({
                name: "",
                part: "",
                cycle: "",
                device: "",
                dateRange: [new Date(), new Date()],
            });
        }
    }, [isOpen, type, jobData]);

    const fetchParts = async () => {
        try {
            const query = {
                query: `
        {
          parts(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            return result.data?.parts || [];
        } catch (error) {
            console.error("Error fetching job parts:", error);
            return [];
        }
    };

    const initializeDropdownData = async () => {
        setLoading(true);
        const [parts, cycles, devices] = await Promise.all([
            fetchParts(),
            fetchCycles(),
            fetchDevices(),
        ]);
        setJobParts(parts);
        setJobCycles(cycles);
        setJobDevices(devices);
        setLoading(false);
    };

    useEffect(() => {
        if (isOpen) {
            initializeDropdownData();
        }
    }, [isOpen]);

    useEffect(() => {
        // Auto-generate name when any field changes (but not if editing)
        if (
            type !== "edit" &&
            jobDevices.length > 0 &&
            jobParts.length > 0 &&
            jobCycles.length > 0
        ) {
            const selectedDevice = jobDevices.find(
                (d) => d.id === formData.device
            );
            const selectedPart = jobParts.find(
                (p) => String(p.id) === String(formData.part)
            );
            const selectedCycle = jobCycles.find(
                (c) => String(c.id) === String(formData.cycle)
            );
            const [start, end] = formData.dateRange;

            let generatedName = "";

            if (selectedDevice) {
                generatedName = `${selectedDevice.id}`;
            }

            if (selectedPart) {
                generatedName += ` recording ${selectedPart.name}`;
            }

            if (selectedCycle) {
                generatedName += ` on cycle ${selectedCycle.name}`;
            }

            if (start && end) {
                const startDate = new Date(start).toLocaleDateString();
                const endDate = new Date(end).toLocaleDateString();
                generatedName += ` between ${startDate} and ${endDate}`;
            }

            // Only update if name actually changed to avoid infinite loops
            if (generatedName !== previousNameRef.current) {
                previousNameRef.current = generatedName;
                setFormData((prev) => ({ ...prev, name: generatedName }));
            }
        }
    }, [
        formData.device,
        formData.part,
        formData.cycle,
        formData.dateRange,
        type,
        jobDevices,
        jobParts,
        jobCycles,
    ]);

    const fetchCycles = async () => {
        try {
            const query = {
                query: `
        {
          cycles(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            return result.data?.cycles || [];
        } catch (error) {
            console.error("Error fetching job cycles:", error);
            return [];
        }
    };

    const fetchDevices = async () => {
        try {
            const query = {
                query: `
        {
          shadowDevices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            installed_at_zones {
              name
            }
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            return result.data?.shadowDevices || [];
        } catch (error) {
            console.error("Error fetching devices:", error);
            return [];
        }
    };

    const postFormData = async (data) => {
        setLoading(true);
        try {
            if (!lastUsedWorkspace || !lastUsedWorkspace.engine) return;
            const query = {
                query: `
    mutation CreateOneJobSchedule {
      createOneJobSchedule(data: {
        name: "${data.name}"
        start_time: "${data.start.toISOString()}"
        end_time: "${data.end.toISOString()}"
        plant: {
          connect: { id: ${lastUsedPlant.id} }
        }
        part: {
          connect: { id: ${data.part} }
        }
        cycle: {
          connect: { id: ${data.cycle} }
        }
        device: {
          connect: { id: "${data.device}" }
        }
        created_by: {
          connect: { id: ${starekContext.user.id} }
        }
        updated_by: {
          connect: { id: ${starekContext.user.id} }
        }
      }) {
        id
        name
        start_time
        end_time
      }
    }
  `,
            };

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
        } catch (error) {
            console.error("Error creating job schedule:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateFormData = async (data) => {
        setLoading(true);
        try {
            const query = {
                query: `
        mutation UpdateOneJobSchedule {
          updateOneJobSchedule(
            where: { id: ${jobData.id} }
            data: {
              name: { set: "${data.name}" }
              start_time: { set: "${data.start.toISOString()}" }
              end_time: { set: "${data.end.toISOString()}" }
              part: { connect: { id: ${data.part} } }
              cycle: { connect: { id: ${data.cycle} } }
              device: { connect: { id: "${data.device}" } }
              updated_by: { connect: { id: ${starekContext.user.id} } }
            }
          ) {
            id
            name
          }
        }
      `,
            };

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });

            const result = await response.json();
        } catch (error) {
            console.error("Error updating job schedule:", error);
        } finally {
            setLoading(false);
        }
    };

    const deleteJobSchedule = async () => {
        if (!jobData?.id) return;
        setDeleting(true);
        setError(null);
        try {
            const query = {
                query: `
        mutation DeleteOneJobSchedule {
          deleteOneJobSchedule(where: { id: ${jobData.id} }) {
            id
            name
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            if (result.data?.deleteOneJobSchedule) {
                setShowDeleteConfirm(false);
                onClose();
                onSubmit();
            } else {
                throw new Error("Delete failed");
            }
        } catch (err) {
            setError("Failed to delete job schedule");
        } finally {
            setDeleting(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        const [start, end] = formData.dateRange;
        setError(null);

        if (
            !formData.name ||
            !formData.part ||
            !formData.cycle ||
            !formData.device ||
            !start ||
            !end
        ) {
            alert("All fields are mandatory.");
            return;
        }

        const payload = { ...formData, start, end };

        try {
            if (type === "edit") {
                await updateFormData(payload);
            } else {
                await postFormData(payload);
            }
            onSubmit();
            onClose(); // Close sidebar
        } catch (err) {
            console.error("Form submission error:", err);
            setError("Something went wrong. Please try again.");
        }
    };

    return (
        <>
            {loading ? (
                <></>
            ) : (
                <>
                    <Offcanvas
                        show={isOpen}
                        onHide={onClose}
                        placement="end"
                        backdrop={true}
                        style={{
                            width: "580px",
                            overflow: "visible",
                            zIndex: 1050,
                        }}
                    >
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>
                                <h2 className="tw-text-lg tw-font-semibold">
                                    {type === "edit"
                                        ? "Edit Job Schedule"
                                        : "Add Job Schedule"}
                                </h2>
                            </Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body className="tw-flex tw-flex-col tw-h-full tw-overflow-visible tw-relative">
                            <div className="tw-flex-grow tw-flex tw-flex-col tw-gap-4 tw-overflow-auto">
                                <div>
                                    <label className="tw-text-sm tw-font-medium">
                                        Name
                                    </label>
                                    <textarea
                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                        rows="4"
                                        value={formData.name}
                                        onChange={(e) =>
                                            handleChange("name", e.target.value)
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="tw-text-sm tw-font-medium">
                                        Select Device
                                    </label>
                                    <select
                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                        value={formData.device}
                                        onChange={(e) =>
                                            handleChange(
                                                "device",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Select Device</option>
                                        {jobDevices?.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.id}(
                                                {d.installed_at_zones?.[0]
                                                    ?.name || "No Zone"}
                                                )
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="tw-text-sm tw-font-medium">
                                        Select Part
                                    </label>
                                    <select
                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                        value={formData.part}
                                        onChange={(e) =>
                                            handleChange("part", e.target.value)
                                        }
                                    >
                                        <option value="">Select Part</option>
                                        {jobParts?.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="tw-text-sm tw-font-medium">
                                        Select Cycle
                                    </label>
                                    <select
                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                        value={formData.cycle}
                                        onChange={(e) =>
                                            handleChange(
                                                "cycle",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Select Cycle</option>
                                        {jobCycles?.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="tw-text-sm tw-font-medium">
                                        Select Date & Time Range
                                    </label>
                                    <div className="tw-overflow-visible tw-relative">
                                        <DateRangePicker
                                            format="yyyy-MM-dd HH:mm"
                                            value={formData.dateRange}
                                            onChange={(range) => {
                                                if (range) {
                                                    handleChange(
                                                        "dateRange",
                                                        range
                                                    );
                                                }
                                            }}
                                            block
                                            placement="auto"
                                            container={null}
                                            popupClassName="rsuite-datepicker-override"
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                </div>
                            </div>
                            {showDeleteConfirm && (
                                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                                    <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-w-[400px] tw-relative">
                                        <h2 className="tw-text-xl tw-font-bold mb-4">
                                            Delete Confirmation?
                                        </h2>
                                        <p className="tw-mb-4">
                                            Are you sure you want to delete the
                                            job? Once done, the information
                                            cannot be retrieved.
                                        </p>
                                        <p className="tw-mb-6">
                                            <span className="tw-font-medium">
                                                Part Name:
                                            </span>{" "}
                                            {jobData?.name}
                                        </p>
                                        <div className="tw-flex tw-justify-between">
                                            <button
                                                className="tw-px-4 tw-py-2 tw-bg-gray-300 tw-rounded-md"
                                                onClick={() =>
                                                    setShowDeleteConfirm(false)
                                                }
                                                disabled={deleting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="tw-px-4 tw-py-2 tw-bg-red-600 tw-text-white tw-rounded-md tw-inline-flex tw-items-center tw-gap-2"
                                                onClick={deleteJobSchedule}
                                                disabled={deleting}
                                            >
                                                {deleting && (
                                                    <svg
                                                        className="tw-animate-spin tw-h-4 tw-w-4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="tw-opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="tw-opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8v8z"
                                                        ></path>
                                                    </svg>
                                                )}
                                                Yes, Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="tw-text-red-600 tw-mb-2">
                                    {error}
                                </div>
                            )}
                            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                <div>
                                    {type === "edit" && (
                                        <button
                                            className="tw-px-4 tw-py-2  tw-text-red-700 tw-rounded-md hover:tw-underline"
                                            onClick={() =>
                                                setShowDeleteConfirm(true)
                                            }
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>

                                <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                                    <button
                                        className="tw-px-4 tw-py-2 tw-text-gray-600 tw-bg-gray-200 tw-rounded-md"
                                        onClick={onClose}
                                    >
                                        Discard
                                    </button>
                                    <button
                                        className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md tw-inline-flex tw-items-center tw-gap-2"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading && (
                                            <svg
                                                className="tw-animate-spin tw-h-4 tw-w-4 tw-text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="tw-opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="tw-opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v8z"
                                                ></path>
                                            </svg>
                                        )}
                                        {type === "edit" ? "Update" : "Add"}
                                    </button>
                                </div>
                            </div>
                        </Offcanvas.Body>
                    </Offcanvas>{" "}
                </>
            )}
        </>
    );
};

export default JobSchedulesSidebar;
